import { Grid, Link } from '@mui/material';
import { DataGrid, GridColDef, gridClasses, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useEffect, useState, useCallback } from 'react';
import {
  InvoiceDTO,
  getInvoiceAllList,
  saveColumnSettings,
  ColumnSettingsDTO,
  ColumnSettings,
  getColumnSettings,
} from 'wisestep-api';
import {
  EmptyTeamsAndUsersIcon,
  getDateDifferenceInDay,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  getDateUsingFormat,
} from 'wisestep-base';

import NoDataFoundCard from '../../../components/noDataFoundCard';
import { CustomToolbar } from '../../invoicing/components/DataGridCustomToolBar';
import { IInvoiceList } from '../helper/payload';

const ClosureInvoices = ({ invoiceList }: { invoiceList: IInvoiceList[] }) => {
  const [clientListData, setClientListData] = useState<InvoiceDTO[]>([]);
  const tableData: Record<string, unknown>[] = [];
  const [pageSize, setPageSize] = useState<number>(25);
  const [settings, setSettings] = useState<GridColumnVisibilityModel | null>(null);

  const filterByReference = useCallback((arr1, arr2) => {
    const invoiceIds = arr1.filter((el) => {
      return arr2.find((el2) => {
        return el.id === el2.id;
      });
    });
    setClientListData(invoiceIds);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getColumnSettings(ColumnSettings.ORAGANISATION_ENTITY);
        setSettings(res.data.settings);
      } catch (error) {
        setSettings({
          clientInvoiceTypename: true,
          clientname: true,
          dueDate: true,
          invoiceCode: true,
          invoiceStatus: true,
          empGuaranteeStatus: true,
          raisedDate: true,
          remainingAmount: true,
          total: true,
        });
      }
    };
    if (settings === null) {
      fetchData();
    }
  }, [settings]);

  const onColumnChange = async (data: GridColumnVisibilityModel) => {
    const settingsPayload: ColumnSettingsDTO = {
      settings: data,
      tableKey: ColumnSettings.ORAGANISATION_ENTITY,
    };
    await saveColumnSettings(settingsPayload);
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await getInvoiceAllList();
      filterByReference(response.data, invoiceList);
    };
    if (invoiceList?.length > 0) {
      fetchData();
    }
  }, [filterByReference, invoiceList, invoiceList?.length]);

  const settingInvoiceStatus = (date: string) => {
    let status = 'NA';
    if (date) {
      const dayDiff = getDateDifferenceInDay(date, new Date().toDateString());
      if (dayDiff > 0) {
        status = `Due in ${dayDiff} Days`;
      } else if (dayDiff < 0) {
        status = `Overdue by ${dayDiff * -1} Days`;
      } else {
        status = 'Due Today';
      }
    }
    return status;
  };
  clientListData?.map((item) => {
    tableData.push({
      id: item.id,
      clientname: item.client.name,
      invoiceCode: item.invoiceCode,
      clientInvoiceTypename: item.clientInvoiceType.name,
      raisedDate: getDateUsingFormat(item?.raisedDate.toString(), 'DD-MMM-YY'),
      dueDate: getDateUsingFormat(item?.dueDate.toString(), 'DD-MMM-YY'),
      invoiceStatus: settingInvoiceStatus(item.dueDate ? item.dueDate.toString() : ''),
      total: (item.currencyEntity?.symbol ?? '') + item.total,
      remainingAmount: (item.currencyEntity?.symbol ?? '') + item.remainingAmount,
    });
  });
  const columns: GridColDef[] = [
    {
      field: 'invoiceCode',
      headerName: 'Invoice #',
      flex: 1,
      renderCell: (params) => (
        <Link underline="none" href={`/invoice/${params.id}/`} target="_blank">
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            cursor="pointer"
            bold
            color={WsTypoColors.PRIMARY}
          >
            {params.row['invoiceCode'] ?? 'NA'}
          </WsTypography>
        </Link>
      ),
    },
    {
      field: 'clientname',
      headerName: 'Client Name',
      flex: 1,
    },
    {
      field: 'clientInvoiceTypename',
      headerName: 'Invoice Category',
      flex: 1,
    },
    {
      field: 'raisedDate',
      headerName: 'Date',
      flex: 1,
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      flex: 1,
    },
    {
      field: 'invoiceStatus',
      headerName: 'Invoice Status',
      flex: 1,
    },
    {
      field: 'total',
      headerName: 'Invoice Amount',
      flex: 1,
    },
    {
      field: 'remainingAmount',
      headerName: 'Amount Due',
      flex: 1,
    },
  ];
  return (
    <Grid container sx={{ display: 'flex' }} mt={2}>
      {settings != null && tableData && tableData.length > 0 ? (
        <Grid xs={12} sx={{ backgroundColor: 'white' }}>
          <DataGrid
            autoHeight={true}
            components={{ Toolbar: CustomToolbar }}
            columns={columns}
            rows={tableData}
            pageSize={pageSize}
            rowsPerPageOptions={[25, 50, 75, 100]}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            getRowHeight={() => 'auto'}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            columnVisibilityModel={settings}
            onColumnVisibilityModelChange={(data) => {
              onColumnChange(data);
              setSettings(data);
            }}
          />
        </Grid>
      ) : (
        <NoDataFoundCard text="No results found." icon={<EmptyTeamsAndUsersIcon />} />
      )}
    </Grid>
  );
};

export default ClosureInvoices;
